'use client';

import { useMemo, useState } from 'react';

import { DateTimeInUTC } from '@/components/atoms/datetime';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { EventRow } from '@/components/routes/event-row';
import { RouteSummary } from '@/components/routes/route-summary';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCustomerName } from '@/lib/format';
import { cn } from '@/lib/utils';

interface RouteDetailsSheetProps {
  routeId: string;
  /** Trigger content for opening the sheet */
  trigger: React.ReactNode;
}

type Route = ApiSchemas['RouteReadComplete'];
type Event = Route['events'][number];

export function RouteDetailsSheet({ routeId, trigger }: RouteDetailsSheetProps) {
  const [open, setOpen] = useState(false);
  const { api } = useApiV1Ctx();
  const { data, isLoading } = api.routes.getRoutesRouteIdGet.useQuery({
    path: { route_id: routeId },
  });

  const groups = useMemo(() => {
    const events = data?.events ?? [];
    const unscheduled: Event[] = [];
    const scheduled: Event[] = [];
    const rescheduled: Event[] = [];
    const cancelled: Event[] = [];

    const sorted = [...events].sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? ''));

    for (const ev of sorted) {
      const status = ev.service_event?.status ?? 'PENDING';
      if (status === 'CANCELLED') {
        cancelled.push(ev);
        continue;
      }
      if (!ev.start_time) {
        unscheduled.push(ev);
        continue;
      }
      if (status === 'RESCHEDULED') {
        rescheduled.push(ev);
        continue;
      }
      scheduled.push(ev);
    }

    return { unscheduled, scheduled, cancelled, rescheduled };
  }, [data]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        className='w-full gap-2 overflow-y-auto pt-2 sm:max-w-xl md:top-4 md:right-4 md:h-[calc(100%-2rem)] md:rounded-lg'
        showClose
      >
        <SheetHeader className='flex flex-row items-center justify-between gap-2'>
          <div className='flex flex-col'>
            <SheetTitle className='text-sm'>Route details</SheetTitle>
            <SheetDescription>
              {data?.start_at && <DateTimeInUTC value={data.start_at} format='EEEE, MMM d' />}
            </SheetDescription>
          </div>
        </SheetHeader>

        {isLoading && <Skeleton className='mx-4 mb-6 h-60' />}

        {!isLoading && data && (
          <div className='flex flex-col gap-4 px-4 pb-8'>
            <RouteSummary route={data} />

            {groups.unscheduled.length > 0 && (
              <div className='flex flex-col gap-2'>
                <div className='px-1 text-xs text-muted-foreground'>Unscheduled</div>
                <EventList events={groups.unscheduled} />
              </div>
            )}

            {groups.scheduled.length > 0 && (
              <div className='flex flex-col gap-2'>
                <div className='px-1 text-xs text-muted-foreground'>Schedule</div>
                <EventList events={groups.scheduled} />
              </div>
            )}

            {groups.rescheduled.length > 0 && (
              <div className='flex flex-col gap-2'>
                <div className='px-1 text-xs text-muted-foreground'>Rescheduled</div>
                <EventList events={groups.rescheduled} />
              </div>
            )}

            {groups.cancelled.length > 0 && (
              <div className='flex flex-col gap-2'>
                <div className='px-1 text-xs text-muted-foreground'>Cancelled</div>
                <EventList events={groups.cancelled} className='bg-muted/30' />
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

interface EventListProps {
  className?: string;
  events: Event[];
}

function EventList({ events, className }: EventListProps) {
  return (
    <div className={cn('flex flex-col overflow-hidden rounded-md border', className)}>
      {events.map((ev) => (
        <EventRowWrapper key={ev.id} event={ev} />
      ))}
    </div>
  );
}

interface EventRowProps {
  event: Event;
}

function EventRowWrapper({ event }: EventRowProps) {
  const customer = event.service_event?.customer;
  const title = customer ? formatCustomerName(customer) : event.non_service_event?.name;
  return (
    <EventRow
      type={event?.service_event ? 'service' : 'non-service'}
      start_time={event.start_time}
      end_time={event.end_time}
      duration_minutes={event.duration_minutes}
      window_start_time={event.window_start_time}
      window_end_time={event.window_end_time}
      title={title}
      status={event.service_event?.status ?? null}
    />
  );
}
